import { useState, useEffect, Suspense, lazy } from "react";
import { useLocation } from "@/components/location-provider";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageHeader } from "@/components/page-header";
import { MapPin, Zap } from "lucide-react";
import type { LightningCell } from "@/components/map/weather-map";

const WeatherMap = lazy(() => import("@/components/map/weather-map").then((m) => ({ default: m.WeatherMap })));

const LAYERS = [
  { value: "temp_new", label: "Temperature" },
  { value: "clouds_new", label: "Clouds" },
  { value: "precipitation_new", label: "Precipitation" },
  { value: "wind_new", label: "Wind" },
  { value: "pressure_new", label: "Pressure" },
];

export default function MapPage() {
  const { current: location } = useLocation();
  const [layer, setLayer] = useState("temp_new");
  const [lightningOn, setLightningOn] = useState(false);
  const [lightning, setLightning] = useState<LightningCell[]>([]);
  const [lightningLoading, setLightningLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("vertali_prefs");
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        if (prefs.mapLayer) setLayer(prefs.mapLayer);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("vertali_prefs");
    const prefs = saved ? JSON.parse(saved) : {};
    prefs.mapLayer = layer;
    localStorage.setItem("vertali_prefs", JSON.stringify(prefs));
  }, [layer]);

  useEffect(() => {
    if (!lightningOn) {
      setLightning([]);
      return;
    }
    setLightningLoading(true);
    fetch(`/api/lightning?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => setLightning((d.cells || []) as LightningCell[]))
      .catch(() => setLightning([]))
      .finally(() => setLightningLoading(false));
  }, [lightningOn, location.lat, location.lon]);

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Explore"
        title="Weather Map"
        subtitle={`${location.name}, ${location.country}`}
        action={
          <GlassPanel intensity="low" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs !rounded-full">
            <MapPin className="h-3 w-3 text-primary" />
            <span className="font-semibold">{location.name}</span>
          </GlassPanel>
        }
      />

      <div className="flex flex-wrap gap-2">
        {LAYERS.map((l) => (
          <button
            key={l.value}
            onClick={() => setLayer(l.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border ${
              layer === l.value
                ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20"
                : "bg-background/60 backdrop-blur-sm border-border/40 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            {l.label}
          </button>
        ))}
        <button
          onClick={() => setLightningOn((v) => !v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border inline-flex items-center gap-1.5 ${
            lightningOn
              ? "bg-amber-500 text-black border-amber-500 shadow-sm shadow-amber-500/30"
              : "bg-background/60 backdrop-blur-sm border-border/40 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          }`}
        >
          <Zap className={`h-3 w-3 ${lightningOn ? "" : "text-amber-400"}`} />
          Lightning
          {lightningLoading && <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />}
        </button>
      </div>

      {lightningOn && lightning.length === 0 && !lightningLoading && (
        <p className="text-[11px] text-muted-foreground/70">
          No lightning potential detected within ~50 km. Only available for HRRR (North America) / ICON-D2 (Central Europe) areas.
        </p>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <Suspense fallback={<div className="h-96 flex items-center justify-center text-muted-foreground">Loading map...</div>}>
          <WeatherMap lat={location.lat} lon={location.lon} layer={layer} lightning={lightning} />
        </Suspense>
      </div>
    </div>
  );
}